/**
 * Router-level allowlist enforcement for location viewers — covers every
 * route KIND, not just the `kind:"page"` entries RouteGateGuard wraps.
 * A `kind:"redirect"` entry (e.g. /insights -> /) or the "*" NotFound
 * catch-all bypasses RouteGateGuard entirely, so a location viewer landing
 * on one of those got a stuck URL and an empty render — no redirect ever
 * fired (live-smoke finding, Phase 3 follow-up,
 * docs/plans/2026-07-30-location-login.md). This operates on the live
 * `location.pathname` instead of a specific registry entry, so it catches
 * those too. Defense in depth, not a replacement — RouteGateGuard's
 * per-page check still runs first and prevents a gated page's own content
 * from flashing before this outer check would redirect.
 *
 * Mounted once around <LocationGate><Layout/></LocationGate> in App.tsx's
 * GuardedLayout. While viewer-kind is still resolving, this renders
 * children unconditionally — LocationGate (the inner wrapper) already
 * owns the loading-branch loader (Task 3 T3 fix); duplicating that check
 * here would just be a second, redundant loader.
 */
import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';

import { isLocationAllowedPathname, LOCATION_VIEWER_HOME_PATH } from '@/routes/locationAllowlist';
import { useLocationSession } from '../hooks/useLocationSession';

export function LocationRouteEnforcer({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { isLocationViewer, isLoading } = useLocationSession();

  if (!isLoading && isLocationViewer && !isLocationAllowedPathname(location.pathname)) {
    return <Navigate to={LOCATION_VIEWER_HOME_PATH} replace />;
  }

  return <>{children}</>;
}
